<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { computed, onMounted, ref } from 'vue';
import { formatDateLabel } from '@lib/utils/formatDateLabel';
import { isOpenNow } from '@lib/utils/isOpenNow';
import { Button } from '@/components/ui/button';
// import { toast } from '@iframe-widgets/pages/widgets/toast/iframeToast';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { CalendarPlus, Moon, Pencil } from 'lucide-vue-next';
import type { WeeklyHours } from '@lib/zod/weeklyScheduleSchema';

const props = defineProps<{
    modelValue?: number | undefined
    weeklyHours: WeeklyHours | null
    timezone: string | null
    /** Label shown in the schedule notification text. Defaults to "Store". */
    notifyLabel?: string
    /** Label shown when closed. Defaults to "Closed". */
    closedLabel?: string
}>()

const emits = defineEmits<{
    (e: 'update:modelValue', payload: number | undefined): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
    passive: true,
    defaultValue: undefined,
    deep: props.modelValue ? false : true,
})

const locale = Intl.DateTimeFormat().resolvedOptions().locale;

onMounted(() => {
    confirmSchedule();
});

const confirmSchedule = () => {
    if (modelValue.value) {
        if (modelValue.value > Date.now()) {
            // do nothing keep the same value
        } else {
            modelValue.value = undefined;
            // toast.warning("unable to schedule order for a time in the past");
        }
    }
}

setInterval(confirmSchedule, 60e3); // check every minute if the schedule is still valid

const isOpen = computed(() => {
    return isOpenNow(props.timezone, props.weeklyHours, modelValue.value);
});

const slots = computed(() => {
    const days = [];
    const now = new Date();
    for (let i = 0; i < 7; i++) {
        const date = new Date(now);
        date.setDate(now.getDate() + i);
        const dayHours: number[] = [];
        for (let j = 0; j < 24; j++) {
            for (let k = 0; k < 4; k++) {
                const slot = new Date(date);
                slot.setHours(j, k * 15, 0, 0);
                if (
                    isOpenNow(props.timezone, props.weeklyHours, slot.getTime()) &&
                    slot.getTime() > now.getTime()
                ) {
                    dayHours.push(slot.getTime());
                }
            }
        }
        days.push({
            date: date.getTime(),
            dayHours
        });
    }
    return days;
});

const isScheduleOpen = ref(false);

const selectSlot = (slot: number) => {
    modelValue.value = slot;
    isScheduleOpen.value = false;
};

const focusSelectedTime = () => {
    setTimeout(() => {
        if (modelValue.value) {
            const element = document.querySelector(`.selected-time-item`);
            if (element) {
                element.scrollIntoView({ behavior: 'auto', block: 'center' });
            }
        }
    }, 100);
}
</script>

<template>
    <Collapsible v-model:open="isScheduleOpen"
                 @update:open="focusSelectedTime"
                 class="border rounded-md bg-card text-card-foreground">
        <CollapsibleTrigger class="w-full hover:bg-muted/50 cursor-pointer p-4 flex justify-between items-center">
            <div>
                <span v-if="modelValue">
                    <CalendarPlus class="inline-block mr-2 min-w-6 min-h-6" />
                    <span :class="{ 'line-through': !isOpen }">
                        <span class="font-bold">
                            {{ formatDateLabel(modelValue) }}</span>
                        {{ new Date(modelValue).toLocaleDateString(locale, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) }}
                    </span>
                    <span class="p-2 bg-destructive text-destructive-foreground rounded-md inline-block"
                          v-if="!isOpen">
                        <Moon class="mr-1 inline-block" />
                        {{ closedLabel ?? 'Closed' }}
                    </span>
                </span>
                <span v-else>
                    Schedule order
                </span>
            </div>
            <Pencil class="inline-block ml-2 min-w-6 min-h-6 transition-transform duration-200"
                    :class="{ 'rotate-180': isScheduleOpen }" />
        </CollapsibleTrigger>
        <CollapsibleContent>
            <div class="px-4 pt-2 pb-1 border-t bg-background">
                <p class="font-bold text-sm text-muted-foreground mb-3">
                    <CalendarPlus class="inline-block mr-1" />
                    {{ notifyLabel }}
                </p>
                <div class="overflow-y-auto max-h-72 scroll-smooth-container">
                    <div v-for="day in slots"
                         :key="day.date!"
                         class="p-2 border-b mb-8">
                        <div class="text-2xl sticky top-0 bg-muted p-2 rounded-md">
                            <span class="font-bold">{{ formatDateLabel(day.date) }}</span>,
                            {{ new Date(day.date).toLocaleDateString(locale, { month: 'short', day: 'numeric' }) }}
                        </div>
                        <div class="flex flex-col gap-2">
                            <div v-for="slot in day.dayHours"
                                 :key="slot">
                                <div class="w-full cursor-pointer hover:underline text-lg p-4 hover:bg-muted/50 rounded-md"
                                     :class="{ 'selected-time-item font-bold border border-foreground rounded-md': modelValue === slot }"
                                     @click="selectSlot(slot)">
                                    {{ new Date(slot).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false }) }}
                                </div>
                            </div>
                            <div class="text-sm text-muted-foreground">
                                <span v-if="day.dayHours.length === 0">
                                    Closed
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="py-3">
                    <Button variant="outline"
                            class="w-full"
                            @click="modelValue = undefined; isScheduleOpen = false">
                        Cancel schedule
                    </Button>
                </div>
            </div>
        </CollapsibleContent>
    </Collapsible>
</template>
